import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    Card,
    Form,
    Spinner,
    Alert,
    Badge,
    ListGroup,
    Row,
    Col,
} from 'react-bootstrap';

import { useGraphApi } from '@/shared/services/graphApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';

const SECTOR_LABELS = {
    'Non-profit': 'Non-profit',
    'PreK-12 education': 'PreK-12',
    'Post-secondary/Higher education': 'Higher Ed',
    'Business/Industry': 'Business',
    'Government/Policy maker': 'Government',
    'Non formal education': 'Non-formal Ed',
    'Coalition/Alliance': 'Coalition',
    'Foundation/Funder': 'Foundation',
    'Individual/Community': 'Individual',
    'Professional organization': 'Professional',
    'Medical/Health care': 'Health',
    'National lab': 'National Lab',
    Military: 'Military',
    Agriculture: 'Agriculture',
    'Sports organization': 'Sports',
};

function getSectorLabel(sector) {
    return SECTOR_LABELS[sector] || sector;
}

function getStrengthVariant(count) {
    if (count >= 50) return 'success';
    if (count >= 20) return 'primary';
    if (count >= 5) return 'warning';
    return 'secondary';
}

function getStrengthLabel(count) {
    if (count >= 50) return 'Strong';
    if (count >= 20) return 'Moderate';
    if (count >= 5) return 'Emerging';
    return 'New';
}

PartnerCard.propTypes = {
    partnerSector: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    selectedSector: PropTypes.string.isRequired,
    graphApi: PropTypes.object.isRequired,
};

function PartnerCard({ partnerSector, count, selectedSector, graphApi }) {
    const [expanded, setExpanded] = useState(false);
    const [orgs, setOrgs] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleExpand = async () => {
        if (expanded) {
            setExpanded(false);
            return;
        }
        setExpanded(true);

        if (orgs) return;

        setLoading(true);
        try {
            const data = await graphApi.fetchPartnerships(
                selectedSector,
                partnerSector
            );
            setOrgs(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const partnerOrgs = useMemo(() => {
        if (!orgs) return [];
        return [
            ...new Map(
                orgs.nodes
                    .filter((n) => n.primarySector === partnerSector)
                    .map((n) => [n.id, n])
            ).values(),
        ];
    }, [orgs, partnerSector]);

    const yourOrgs = useMemo(() => {
        if (!orgs) return [];
        return [
            ...new Map(
                orgs.nodes
                    .filter((n) => n.primarySector === selectedSector)
                    .map((n) => [n.id, n])
            ).values(),
        ];
    }, [orgs, selectedSector]);

    return (
        <Card className="border-0 shadow-sm mb-3">
            <Card.Body
                className="partnership-card-header"
                onClick={handleExpand}
                role="button"
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <Badge
                            bg="light"
                            text="dark"
                            className="border py-2 px-3"
                        >
                            {getSectorLabel(partnerSector)}
                        </Badge>
                        <Badge
                            bg={getStrengthVariant(count)}
                            className="py-1 px-2"
                        >
                            {getStrengthLabel(count)}
                        </Badge>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">
                            {count} connection{count !== 1 ? 's' : ''}
                        </span>
                        <span className="text-muted">
                            {expanded ? '▲' : '▼'}
                        </span>
                    </div>
                </div>
            </Card.Body>

            {expanded && (
                <Card.Body className="pt-0 border-top">
                    {loading && (
                        <div className="text-center py-3">
                            <Spinner
                                animation="border"
                                size="sm"
                                variant="primary"
                            />
                            <span className="ms-2 text-muted small">
                                Loading organizations...
                            </span>
                        </div>
                    )}
                    {orgs && (
                        <Row className="g-3 mt-1">
                            <Col md={6}>
                                <h6 className="small fw-bold text-muted mb-2">
                                    Your Sector &mdash;{' '}
                                    {getSectorLabel(selectedSector)} (
                                    {yourOrgs.length})
                                </h6>
                                <ListGroup
                                    variant="flush"
                                    className="border rounded"
                                >
                                    {yourOrgs.slice(0, 8).map((org) => (
                                        <ListGroup.Item
                                            key={org.id}
                                            className="py-2"
                                        >
                                            <Link
                                                to={`/organizations/${org.id}`}
                                                className="text-decoration-none"
                                            >
                                                {org.name}
                                            </Link>
                                        </ListGroup.Item>
                                    ))}
                                    {yourOrgs.length > 8 && (
                                        <ListGroup.Item className="py-2 text-muted small">
                                            +{yourOrgs.length - 8} more
                                        </ListGroup.Item>
                                    )}
                                </ListGroup>
                            </Col>
                            <Col md={6}>
                                <h6 className="small fw-bold text-muted mb-2">
                                    Partner Sector &mdash;{' '}
                                    {getSectorLabel(partnerSector)} (
                                    {partnerOrgs.length})
                                </h6>
                                <ListGroup
                                    variant="flush"
                                    className="border rounded"
                                >
                                    {partnerOrgs.slice(0, 8).map((org) => (
                                        <ListGroup.Item
                                            key={org.id}
                                            className="py-2"
                                        >
                                            <Link
                                                to={`/organizations/${org.id}`}
                                                className="text-decoration-none"
                                            >
                                                {org.name}
                                            </Link>
                                        </ListGroup.Item>
                                    ))}
                                    {partnerOrgs.length > 8 && (
                                        <ListGroup.Item className="py-2 text-muted small">
                                            +{partnerOrgs.length - 8} more
                                        </ListGroup.Item>
                                    )}
                                </ListGroup>
                            </Col>
                        </Row>
                    )}
                    {orgs && orgs.edges?.length > 0 && (
                        <p className="text-muted small mt-2 mb-0">
                            {orgs.edges.length} direct partnership
                            {orgs.edges.length !== 1 ? 's' : ''} between these
                            organizations
                        </p>
                    )}
                </Card.Body>
            )}
        </Card>
    );
}

export default function FindPartners() {
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSector, setSelectedSector] = useState('');

    const graphApi = useGraphApi();

    // Publish page context for chat widget
    useChatPageEffect(
        {
            page: 'partners',
            selectedSector: selectedSector || null,
        },
        [selectedSector]
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await graphApi.fetchCollaborationGaps();
                setRawData(result.connections);
            } catch (err) {
                setError('Failed to load partnership data');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [graphApi]);

    // All sectors present in the data
    const allSectors = useMemo(() => {
        if (!rawData) return [];
        const sectors = new Set();
        for (const row of rawData) {
            sectors.add(row.sectorA);
            sectors.add(row.sectorB);
        }
        return [...sectors].sort();
    }, [rawData]);

    // For the selected sector: aggregate connections to each partner sector
    const partnerLandscape = useMemo(() => {
        if (!rawData || !selectedSector) return { connected: [], untapped: [] };

        // Sum connections from selectedSector to each other sector
        const partnerMap = new Map();
        for (const row of rawData) {
            let partner = null;
            if (row.sectorA === selectedSector) partner = row.sectorB;
            if (row.sectorB === selectedSector) partner = row.sectorA;
            if (!partner) continue;

            const current = partnerMap.get(partner) || 0;
            partnerMap.set(partner, current + row.connectionCount);
        }

        // Sectors with connections, sorted by count
        const connected = Array.from(partnerMap.entries())
            .map(([sector, count]) => ({ sector, count }))
            .sort((a, b) => b.count - a.count);

        // Sectors with zero connections to selectedSector
        const connectedSectors = new Set(partnerMap.keys());
        const untapped = allSectors
            .filter((s) => s !== selectedSector && !connectedSectors.has(s))
            .sort();

        return { connected, untapped };
    }, [rawData, selectedSector, allSectors]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading partnership data...</p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div>
            {/* Sector Selection */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                    <h5 className="mb-3">
                        What sector is your organization in?
                    </h5>
                    <p className="text-muted small mb-3">
                        Select your sector to see which organizations you could
                        partner with and where untapped opportunities exist.
                    </p>
                    <Form.Select
                        size="lg"
                        value={selectedSector}
                        onChange={(e) => setSelectedSector(e.target.value)}
                        className="find-partners-selector"
                    >
                        <option value="">Choose your sector...</option>
                        {allSectors.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Form.Select>
                </Card.Body>
            </Card>

            {/* Empty state */}
            {!selectedSector && (
                <div className="text-center py-5">
                    <div className="find-partners-empty-icon mb-3">🔍</div>
                    <h5 className="text-muted">
                        Select your sector to discover partners
                    </h5>
                    <p className="text-muted">
                        We&apos;ll show you which sectors have the strongest
                        connections to yours and where new partnerships could
                        grow.
                    </p>
                </div>
            )}

            {/* Partnership Landscape */}
            {selectedSector && (
                <>
                    {/* Strongest Existing Partnerships */}
                    {partnerLandscape.connected.length > 0 && (
                        <div className="mb-4">
                            <h5 className="mb-1">
                                Your Strongest Partnerships
                            </h5>
                            <p className="text-muted small mb-3">
                                Sectors with existing connections to{' '}
                                {getSectorLabel(selectedSector)}: click to see
                                the organizations involved.
                            </p>
                            {partnerLandscape.connected.map((partner) => (
                                <PartnerCard
                                    key={partner.sector}
                                    partnerSector={partner.sector}
                                    count={partner.count}
                                    selectedSector={selectedSector}
                                    graphApi={graphApi}
                                />
                            ))}
                        </div>
                    )}

                    {/* Untapped Opportunities */}
                    {partnerLandscape.untapped.length > 0 && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white">
                                <strong>Untapped Opportunities</strong>
                                <Badge bg="info" className="ms-2">
                                    {partnerLandscape.untapped.length} sectors
                                </Badge>
                            </Card.Header>
                            <Card.Body>
                                <p className="text-muted small mb-3">
                                    These sectors have no existing connections
                                    to {getSectorLabel(selectedSector)}. Click a
                                    sector to browse its organizations and find
                                    potential partners.
                                </p>
                                <div className="d-flex flex-wrap gap-2">
                                    {partnerLandscape.untapped.map((sector) => (
                                        <Link
                                            key={sector}
                                            to={`/directory?view=cards&sector=${encodeURIComponent(sector)}`}
                                            className="gap-badge-link border rounded p-2 text-decoration-none"
                                        >
                                            {getSectorLabel(sector)}
                                        </Link>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* No connections at all */}
                    {partnerLandscape.connected.length === 0 &&
                        partnerLandscape.untapped.length === 0 && (
                            <Alert variant="info">
                                No cross-sector partnership data found for{' '}
                                {getSectorLabel(selectedSector)}.
                            </Alert>
                        )}
                </>
            )}
        </div>
    );
}
